import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Alert,
  IconButton,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";
import {
  getCurrentUser,
  updateUser,
  toggleUserNotifications,
} from "../../services/auth";

const SettingsPage = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const passwordPattern = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notifMsg, setNotifMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [settings, setSettings] = useState({
    username: "",
    fullName: "",
    email: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    discord_username: "",
    discord_webhook: "",
  });

  const [showSaveAlert, setShowSaveAlert] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      setIsLoading(true);
      try {
        const user = await getCurrentUser();
        setSettings((prev) => ({
          ...prev,
          username: user.username,
          fullName: user.full_name,
          email: user.email,
          discord_username: user.discord_username || "",
          discord_webhook: user.discord_webhook || "",
        }));
        setNotificationsEnabled(user.notifications_enabled !== false);
      } catch (error) {
        setErrorMsg("Fehler beim Laden der Benutzerdaten");
      } finally {
        setIsLoading(false);
      }
    }
    fetchUser();
  }, []);

  const handleToggleNotifications = async () => {
    try {
      const newState = await toggleUserNotifications();
      setNotificationsEnabled(newState.notifications_enabled);
      setNotifMsg(
        newState.notifications_enabled
          ? "Benachrichtigungen aktiviert."
          : "Benachrichtigungen deaktiviert.",
      );
      setTimeout(() => setNotifMsg(""), 3000);
    } catch {
      setNotifMsg("Fehler beim Umschalten der Benachrichtigungen");
      setTimeout(() => setNotifMsg(""), 3000);
    }
  };

  const handleChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    setErrorMsg("");
    setIsLoading(true);
    if (settings.newPassword && !passwordPattern.test(settings.newPassword)) {
      setErrorMsg(
        "Das Passwort muss mindestens 8 Zeichen lang sein, mindestens einen Großbuchstaben und eine Zahl enthalten.",
      );
      setIsLoading(false);
      return;
    }
    if (settings.newPassword !== settings.confirmPassword) {
      setErrorMsg(
        "Das neue Passwort und die Bestätigung stimmen nicht überein",
      );
      setIsLoading(false);
      return;
    }
    try {
      await updateUser({
        username: settings.username,
        full_name: settings.fullName,
        email: settings.email,
        old_password: settings.oldPassword,
        new_password: settings.newPassword,
        discord_username: settings.discord_username,
        discord_webhook: settings.discord_webhook,
      });
      setShowSaveAlert(true);
      setTimeout(() => setShowSaveAlert(false), 3000);
    } catch (error) {
      setErrorMsg(
        error.response?.data?.detail || "Aktualisierung fehlgeschlagen",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <LinearProgress />
            <Typography variant="body1" align="center" sx={{ mt: 2 }}>
              Lade Einstellungen...
            </Typography>
          </Box>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        {/* Header Section */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 3,
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                mr: 3,
                width: 60,
                height: 60,
              }}
            >
              <PersonIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Benutzereinstellungen
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Verwalten Sie Ihre Profilinformationen, Benachrichtigungen und
                Sicherheitseinstellungen.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {errorMsg}
          </Alert>
        )}
        {showSaveAlert && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            <CheckCircleOutline sx={{ mr: 1 }} />
            Benutzerdaten erfolgreich gespeichert!
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Profile Info */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card
              elevation={4}
              sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}
            >
              <Box sx={{ bgcolor: "primary.main", p: 2, color: "white" }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold">
                    Profilinformationen
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <TextField
                  fullWidth
                  label="Benutzername"
                  value={settings.username}
                  onChange={handleChange("username")}
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Vollständiger Name"
                  value={settings.fullName}
                  onChange={handleChange("fullName")}
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={settings.email}
                  onChange={handleChange("email")}
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Discord */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card
              elevation={4}
              sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}
            >
              <Box sx={{ bgcolor: "#7289da", p: 2, color: "white" }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                    <NotificationsIcon />
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold">
                    Benachrichtigungen
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Benachrichtigungen werden Ihnen per E-Mail versendet.
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <MDButton
                    variant={notificationsEnabled ? "contained" : "outlined"}
                    color={notificationsEnabled ? "success" : "warning"}
                    onClick={handleToggleNotifications}
                    size="large"
                    sx={{ minWidth: 200 }}
                  >
                    {notificationsEnabled
                      ? "Benachrichtigungen deaktivieren"
                      : "Benachrichtigungen aktivieren"}
                  </MDButton>
                  <Chip
                    label={notificationsEnabled ? "AN" : "AUS"}
                    color={notificationsEnabled ? "success" : "warning"}
                    variant="filled"
                  />
                </Box>
                {notifMsg && (
                  <Alert
                    severity={notifMsg.includes("Fehler") ? "error" : "info"}
                    sx={{ mt: 2, borderRadius: 2 }}
                  >
                    {notifMsg}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Password Change */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={4} sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ bgcolor: "error.main", p: 2, color: "white" }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2 }}>
                    <LockIcon />
                  </Avatar>
                  <Typography variant="h5" fontWeight="bold">
                    Passwort ändern
                  </Typography>
                </Box>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Box mb={3}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    fontWeight="bold"
                  >
                    Passwortanforderungen:
                  </Typography>
                  <Box display="flex" flexDirection="column" ml={2} gap={1}>
                    <Box display="flex" alignItems="center">
                      <CheckCircleOutline
                        fontSize="small"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        Mindestens 8 Zeichen
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <CheckCircleOutline
                        fontSize="small"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        Mindestens einen Großbuchstaben
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <CheckCircleOutline
                        fontSize="small"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2">
                        Mindestens eine Zahl
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Altes Passwort"
                      type={showOldPassword ? "text" : "password"}
                      value={settings.oldPassword}
                      onChange={handleChange("oldPassword")}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowOldPassword(!showOldPassword)}
                          >
                            {showOldPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Neues Passwort"
                      type={showNewPassword ? "text" : "password"}
                      value={settings.newPassword}
                      onChange={handleChange("newPassword")}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Bestätige neues Passwort"
                      type={showConfirmPassword ? "text" : "password"}
                      value={settings.confirmPassword}
                      onChange={handleChange("confirmPassword")}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={4}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={isLoading}
                    sx={{ minWidth: 200, py: 1.5 }}
                  >
                    {isLoading ? "Speichere..." : "Änderungen speichern"}
                  </MDButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default SettingsPage;
